import { useCallback, useMemo } from "react";
import { useAuthStore } from "@/store/authStore";
import {
  RELEASE_NOTES,
  notesFor,
  unreadNotes,
  useReleaseNotesSeen,
  type ReleaseNote,
} from "@/lib/releaseNotes";

/**
 * Who is reading, and where they had read up to.
 *
 * Roles are the effective ones: an admin seeing the app as a parent gets the
 * parent's feed. The read state is still the admin's own (`user` stays the
 * admin while impersonating), which is why `useWhatsNew` won't record a visit
 * made in someone else's shoes - it is kept against the whole feed, and they
 * have only been shown part of it.
 */
function useReader() {
  const user = useAuthStore((state) => state.user);
  const roles = useAuthStore((state) => state.getEffectiveRoles());
  const isImpersonating = useAuthStore((state) => state.impersonation?.isImpersonating ?? false);
  const seen = useReleaseNotesSeen((state) => (user ? state.seen[user.id] : undefined));
  return { user, roles, isImpersonating, seen };
}

/**
 * The release notes as the signed-in person sees them: filtered to their
 * roles, with what they have not opened yet, and a way to record that they
 * have. The topbar's dot reads the same store, so opening the page clears the
 * dot without a reload.
 */
export function useWhatsNew(feed: ReleaseNote[] = RELEASE_NOTES) {
  const { user, roles, isImpersonating, seen } = useReader();
  const record = useReleaseNotesSeen((state) => state.markSeen);

  const notes = useMemo(() => notesFor(feed, roles), [feed, roles]);
  const unread = useMemo(() => unreadNotes(feed, roles, seen), [feed, roles, seen]);

  const markSeen = useCallback(() => {
    if (!user || isImpersonating || feed.length === 0) return;
    record(user.id, { sha: feed[0].sha, date: feed[0].date });
  }, [user, isImpersonating, feed, record]);

  return { notes, unread, markSeen };
}

/** Just the topbar's question, without building the list it never shows. */
export function useHasUnreadNotes(feed: ReleaseNote[] = RELEASE_NOTES): boolean {
  const { roles, seen } = useReader();
  return useMemo(() => unreadNotes(feed, roles, seen).length > 0, [feed, roles, seen]);
}
